import React, { useState } from 'react';
import { supabase } from '../lib/supabase';
import { callEdgeFunction } from '../lib/api';
import { Send, CheckCircle, Loader2, User, Mail, Building2, Phone, MessageSquare } from 'lucide-react';

interface LeadCaptureFormProps {
  pageSource: string;
  title?: string;
  subtitle?: string;
  accentColor?: string;
}

const LeadCaptureForm: React.FC<LeadCaptureFormProps> = ({
  pageSource,
  title = 'Get Early Access',
  subtitle = 'Join the waitlist and be first to experience the platform',
  accentColor = 'violet',
}) => {
  const [form, setForm] = useState({ name: '', email: '', company: '', phone: '', message: '' });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) return;
    setStatus('loading');
    try {
      const { error } = await supabase.from('lead_captures').insert({
        page_source: pageSource,
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim() || null,
        phone: form.phone.trim() || null,
        message: form.message.trim() || null,
      });
      if (error) throw error;

      // Look up whether this splash page has a linked email sequence
      const { data: leadPage } = await supabase
        .from('lead_capture_pages')
        .select('id, sequence_id, user_id')
        .eq('page_key', pageSource)
        .maybeSingle();

      if (leadPage?.sequence_id && leadPage?.user_id) {
        try {
          await callEdgeFunction('email-drip', {
            action: 'start_sequence',
            sequence_id: leadPage.sequence_id,
            tenant_id: leadPage.user_id,
            subscriber_email: form.email.trim(),
            subscriber_name: form.name.trim(),
          });
        } catch {
          // Drip trigger failed — don't block the success state
        }
      }

      setStatus('success');
      setForm({ name: '', email: '', company: '', phone: '', message: '' });
    } catch {
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <div className="glass-card rounded-2xl p-8 text-center animate-scale-in">
        <div className={`w-16 h-16 rounded-full bg-${accentColor}-100 flex items-center justify-center mx-auto mb-4`}>
          <CheckCircle className={`h-8 w-8 text-${accentColor}-600`} />
        </div>
        <h3 className="text-xl font-bold text-slate-800 mb-2">You're on the list!</h3>
        <p className="text-sm text-slate-500 mb-6">We'll be in touch soon with your exclusive access details.</p>
        <button
          onClick={() => setStatus('idle')}
          className="btn-ghost px-6 py-2.5 text-sm"
        >
          Submit another entry
        </button>
      </div>
    );
  }

  return (
    <div className="glass-card rounded-2xl p-6 md:p-8">
      <div className="text-center mb-6">
        <h3 className="text-xl font-bold text-slate-800 mb-1">{title}</h3>
        <p className="text-sm text-slate-500">{subtitle}</p>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-medium text-slate-500 mb-1.5 block">Full Name *</label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="input-dark w-full pl-10 pr-3 py-2.5 text-sm"
                placeholder="Jane Doe"
              />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 mb-1.5 block">Email *</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="input-dark w-full pl-10 pr-3 py-2.5 text-sm"
                placeholder="jane@company.com"
              />
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-medium text-slate-500 mb-1.5 block">Company</label>
            <div className="relative">
              <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                value={form.company}
                onChange={(e) => setForm({ ...form, company: e.target.value })}
                className="input-dark w-full pl-10 pr-3 py-2.5 text-sm"
                placeholder="Acme Inc."
              />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 mb-1.5 block">Phone</label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="input-dark w-full pl-10 pr-3 py-2.5 text-sm"
                placeholder="+1 (555) 000-0000"
              />
            </div>
          </div>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-500 mb-1.5 block">Message</label>
          <div className="relative">
            <MessageSquare className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
            <textarea
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              rows={3}
              className="input-dark w-full pl-10 pr-3 py-2.5 text-sm resize-none"
              placeholder="Tell us about your marketing goals..."
            />
          </div>
        </div>
        {status === 'error' && (
          <p className="text-xs text-red-500 text-center">Something went wrong. Please try again.</p>
        )}
        <button
          type="submit"
          disabled={status === 'loading'}
          className="btn-primary w-full py-3 text-sm flex items-center justify-center gap-2 disabled:opacity-60"
        >
          {status === 'loading' ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Submitting...
            </>
          ) : (
            <>
              <Send className="h-4 w-4" />
              Request Access
            </>
          )}
        </button>
        <p className="text-[10px] text-slate-400 text-center">We respect your privacy. No spam, ever.</p>
      </form>
    </div>
  );
};

export default LeadCaptureForm;
